import { useMsal } from '@azure/msal-react';
import { Widgets } from '@mui/icons-material';
import { Box, Chip, Paper, Tab, Tabs, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle, { DialogTitleProps } from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import React, { useContext, useEffect, useState } from 'react';
import Api from '../../Api';
import { Feature, PermissionContext } from '../../contexts/PermissionContext';
import { IMaterial, IMaterialDetailsDialogProps, MaterialContext } from '../../interfaces';
import { Role } from '../../utils';
import { MaterialInputDialog } from '../MaterialInputDialog';
import { HistoricMaterialInformationAlert } from '../Shared/HistoricMaterialInformationAlert';
import { TabPanel } from '../Shared/TabPanel';
import { Audits } from './Audits';
import { Dispatches } from './Dispatches';
import { ImageGrid } from './ImageGrid';
import './index.scss';
import { MaterialDetails } from './MaterialDetails';
import { Notes } from './Notes';


const MaterialDialogTitle = styled(DialogTitle, {})<DialogTitleProps>(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white'
}));

export const MaterialDetailsDialog: React.SFC<IMaterialDetailsDialogProps> = ({
    material,
    onChangeTab,
    onReloadMaterial,
    onClose,
    viewMode,
    buttonLabel,
    buttonVariant,
    ...props
}: IMaterialDetailsDialogProps) => {

    const { accounts } = useMsal();

    const permissionContext = useContext(PermissionContext);

    const showExtended = () => {

        let allowedRoles = [Role.Internal.AssetProtection, Role.Internal.Procurement];

        let claims = accounts[0].idTokenClaims as any;
        let roles = claims.roles as string[];

        return allowedRoles.includes(roles[0]);
    }

    const canUpdateMaterial = () => {
        var live = viewMode !== "material_audit_static";
        var permitted = permissionContext.isPermitted(Feature.Inventory_UpdateMaterial);
        return (live && permitted);
    }
    const onUpdateMaterial = async (material: IMaterial) => {
        if (material) {

            setUpdated(true);

            await Api.Materials.update(material).then((response) => {
                let materialResponse = response as IMaterial;
                if (onReloadMaterial && materialResponse.materialId)
                    onReloadMaterial(materialResponse.materialId);

            }).catch((e) => {
                console.log("error", e);
            });
        }
    }
    const getOnUpload = () => {

        var permitted = permissionContext.isPermitted(Feature.Inventory_UpdateMaterial);

        return (viewMode === "material_audit" || permitted) ? onReloadMaterial : undefined;
    }

    const handleClose = () => {
        if (updated && onClose)
            onClose(true);
        setOpen(false);
    }

    const [open, setOpen] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [value, setValue] = useState(0);
    const [storedAssembly, setStoredAssembly] = useState<IMaterial>();

    useEffect(() => {
        if (material.storedAssemblyId)
            Api.Materials.get(material.storedAssemblyId).then(response => {
                setStoredAssembly(response as IMaterial);
            });
    }, [])



    return (
        <span onClick={(e) => e.stopPropagation()}>

            {viewMode === "stored_assembly" ?
                <Tooltip title="View stored assembly">
                    <Chip
                        size="small"
                        icon={<Widgets />}
                        label={material.l3}
                        variant="outlined"
                        color="primary"
                        onClick={() => { setOpen(true) }}
                    />
                </Tooltip>

                :
                <Tooltip title="View material">

                    <Button
                        size="small"
                        variant={buttonVariant !== undefined ? buttonVariant : "contained"}
                        color="primary"
                        onClick={() => setOpen(true)}
                    >
                        {buttonLabel !== undefined ? buttonLabel : "View"}
                    </Button>
                </Tooltip>
            }
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="xl"
            >

                <MaterialDialogTitle>
                    {material.l3} <small>({material.l1?.value}, {material.l2?.value})</small>
                    {canUpdateMaterial() &&

                        <Paper
                            sx={{
                                display: 'inline',
                                lineHeight: 'normal',
                                padding: "1px",
                                float: 'right'
                            }}
                            elevation={4}
                        >

                            <MaterialInputDialog
                                context={MaterialContext.Inventory}
                                entity={{ id: material.materialId ?? "", type: "SEARCHRESULT" }}
                                material={material}
                                focus={true}
                                onSubmitMaterial={onUpdateMaterial}
                            />


                        </Paper>

                    }

                </MaterialDialogTitle>



                <DialogContent sx={{ pb: 0 }}>
                    {(viewMode === "material_audit_static") &&
                        <HistoricMaterialInformationAlert />
                    }
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={value} onChange={(_, value) => setValue(value)} aria-label="material-detail-tabs">
                            <Tab label="Material Details" id={`details-tab-${0}`} />
                            <Tab label="Photos" id={`details-tab-${1}`} />
                            <Tab label="Notes" id={`details-tab-${2}`} />
                            {showExtended() && <Tab label="Audit History" id={`details-tab-${3}`} />}
                            {showExtended() && <Tab label="Dispatch History" id={`details-tab-${4}`} />}
                        </Tabs>
                    </Box>

                    <TabPanel value={value} index={0}>
                        <MaterialDetails
                            material={material}
                            onChangeTab={(index: number) => setValue(index)}
                            viewMode={viewMode}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={1}>
                        <ImageGrid
                            materialId={material.materialId}
                            onUpload={getOnUpload}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={2}>
                        <Notes
                            material={material}
                            onReloadMaterial={viewMode === "material_audit" ? onReloadMaterial : undefined}
                            viewMode={viewMode}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={3}>

                        <Audits
                            load={value === 3}
                            material={material}
                        />

                    </TabPanel>
                    <TabPanel value={value} index={4}>
                        <Dispatches
                            load={value === 4}
                            material={material}
                        />
                    </TabPanel>

                </DialogContent>
                <DialogActions>

                    {storedAssembly &&
                        <Box width="100%" pl={2} >
                            <MaterialDetailsDialog
                                material={storedAssembly}
                                viewMode="stored_assembly"
                            />
                        </Box>
                    }

                    <Button onClick={handleClose} color="primary">Close</Button>

                </DialogActions>

            </Dialog>
        </span>
    );
}